import { AxiosResponse } from 'axios';
import { saveAs } from 'file-saver';

import {
  PageOfQueryDto,
  QueryDto,
  ReportFieldConfiguration,
  SearchQuerySaveRequest,
} from '../../generated/model/models';
import { RequestParams } from '../../pages/FinancialData/shared/FinancialDataContext/FinancialDataContext.types';
import { httpClient } from '../config/httpClient';
import { parseFilename } from '../utils/axios.utils';
import { makeUrl } from '../utils/routes.utils';

export const path = {
  fetchFilters: '/search/filter/fetch',
  searchQueries: '/search/query/fetch',
  searchAdminQueries: '/search/query/admin/fetch',
  saveFilter: '/search/filter/save',
  deleteSelected: '/search/queries',
  saveQuery: '/search/query/save',
  fetchQuery: '/search/{searchId}',
  rawReport: '/report/RAW/{searchId}/xlsx',
  marketingReport: 'report/MARKETING/{searchId}/xlsx',
  downloadSelected: 'report/all/xlsx',
};

export enum QueryTypeEnum {
  FILTER_CONFIGURATION = 'FILTER_CONFIGURATION',
  COMPANY_SEARCH = 'COMPANY_SEARCH',
}

export const getMyFilters = async (params: any): Promise<PageOfQueryDto> => {
  const sortParams = ['name', 'asc'];
  const response = await httpClient.get<PageOfQueryDto>(path.fetchFilters, {
    params: {
      sort: sortParams.toString(),
      type: QueryTypeEnum.FILTER_CONFIGURATION,
      ...params,
    },
  });
  return response.data;
};

export const getPredefinedFilters = async (params: any): Promise<PageOfQueryDto> => {
  const response = await httpClient.get<PageOfQueryDto>(path.fetchFilters, {
    params: {
      predefined: true,
      ...params,
    },
  });
  return response.data;
};

export const getPagedQueries = async (params: any): Promise<PageOfQueryDto> => {
  const response = await httpClient.get<PageOfQueryDto>(path.searchQueries, { params });
  return response.data;
};

export const getPagedAdminQueries = async (params: any): Promise<PageOfQueryDto> => {
  const response = await httpClient.get<PageOfQueryDto>(path.searchAdminQueries, { params });
  return response.data;
};

export enum SearchReportTypes {
  RAW = 'RAW',
  MARKETING = 'MARKETING',
}

const searchReportsUrl = {
  [SearchReportTypes.RAW]: path.rawReport,
  [SearchReportTypes.MARKETING]: path.marketingReport,
};

export const downloadReport = async (reportType: SearchReportTypes, searchId: string) => {
  const endpoint = makeUrl(searchReportsUrl[reportType], { searchId });
  const response = await httpClient.get<Blob>(endpoint, { responseType: 'blob' });
  const blob = new Blob([response.data]);

  const filename = parseFilename(response.headers['content-disposition']);
  saveAs(blob, filename);
};

export const downloadSelectedReports = async (data: string[]) => {
  const response = await httpClient.post<Blob>(path.downloadSelected, data, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data]);

  const filename = parseFilename(response.headers['content-disposition']);
  saveAs(blob, filename);
};

export const deleteSelectedReports = async (data: string[], callback: () => void): Promise<any> => {
  await httpClient.delete(path.deleteSelected, { data });
  callback && callback();
};

export const getReportByCompanyId = async (
  reportType: SearchReportTypes,
  companyId: string,
  searchId?: string,
  config?: ReportFieldConfiguration,
): Promise<any> => {
  const url = searchId
    ? path.saveQuery + `/${companyId}?searchId=${searchId}`
    : path.saveQuery + `/${companyId}`;

  const queryResponse: AxiosResponse = await httpClient.post<SearchQuerySaveRequest>(url, config);

  return queryResponse ? await downloadReport(reportType, queryResponse.data) : {};
};

export const getBothReportsById = async (companyId: string, searchId?: string) => {
  let response: any = {};
  const url = searchId
    ? path.saveQuery + `/${companyId}?searchId=${searchId}`
    : path.saveQuery + `/${companyId}`;

  const queryResponse: AxiosResponse = await httpClient.post<SearchQuerySaveRequest>(url);

  if (queryResponse) {
    response = await downloadSelectedReports([queryResponse.data]);
  }
  return response;
};

export const getReport = async (
  reportType: SearchReportTypes,
  queryRequest: any,
  params?: RequestParams,
): Promise<any> => {
  const queryResponse: AxiosResponse = await httpClient.post<SearchQuerySaveRequest>(
    path.saveQuery,
    queryRequest,
    { params },
  );

  return queryResponse ? await downloadReport(reportType, queryResponse.data) : {};
};

export const getBothReports = async (queryRequest: any, params?: RequestParams): Promise<any> => {
  let response: any = {};

  const queryResponse: AxiosResponse = await httpClient.post<SearchQuerySaveRequest>(
    path.saveQuery,
    queryRequest,
    { params },
  );

  if (queryResponse) {
    response = await downloadSelectedReports([queryResponse.data]);
  }
  return response;
};

export const getQuery = async (searchId: string): Promise<QueryDto> => {
  const response = await httpClient.get<QueryDto>(makeUrl(path.fetchQuery, { searchId }));
  return response.data;
};
